/**
 * Utility for handling conditional content based on Need-Based Grant Estimate
 */

const NEED_BASED_GRANT_NAMES = {
  name: "Need-Based Grant Estimate",
  friendlyName: "need_based_grant_estimate",
} as const;

const ENABLING_FORMULA = "need_based_grant_estimate>0";
const STORAGE_KEY = "needBasedGrantEstimate";

/**
 * Find and return conditional description for attributes with enabling formula
 * @param sortedSummaryAttrs - Sorted summary attributes
 * @param derivedAttributesData - Derived attributes data from API
 * @param needBasedGrantValue - Current value of need-based grant estimate
 * @returns Conditional description string or empty string
 */
export const getConditionalDescription = (
  sortedSummaryAttrs: any[],
  derivedAttributesData: any,
  needBasedGrantValue: number
): string => {
  if (needBasedGrantValue <= 0) {
    return "";
  }

  // Search in sortedSummaryAttrs for attribute with enablingFormula
  const conditionalAttr = sortedSummaryAttrs.find(
    (attr: any) =>
      attr.enablingFormula === ENABLING_FORMULA ||
      attr.enablingFormula?.toLowerCase().includes(ENABLING_FORMULA)
  );

  if (conditionalAttr?.description) {
    return conditionalAttr.description;
  }

  // Fallback: search in all derived attributes
  const summaryObject = derivedAttributesData?.objects?.find(
    (obj: any) => obj.name === "Summary"
  );
  const summaryDerivedAttrs = summaryObject?.attributes || [];

  const conditionalDerivedAttr = summaryDerivedAttrs.find(
    (attr: any) =>
      attr.enablingFormula === ENABLING_FORMULA ||
      attr.enablingFormula?.toLowerCase().includes(ENABLING_FORMULA)
  );

  return conditionalDerivedAttr?.description || "";
};

/**
 * Extract the value from a Need-Based Grant attribute
 * @param attr - The attribute object
 * @returns The extracted value or null
 */
const extractValue = (attr: any): string | null => {
  return (
    attr.value ||
    attr.calculatedValue ||
    attr.displayValue ||
    attr.defaultValue ||
    attr.constantValue ||
    attr.operands?.[0]?.value ||
    null
  );
};

/**
 * Extract and store Need-Based Grant Estimate value
 * @param allDerivedAttributes - All derived attributes from API
 * @returns The value stored or null if not found
 */
export const extractAndStoreNeedBasedGrant = (
  allDerivedAttributes: any[]
): string | null => {
  const needBasedGrantAttr = allDerivedAttributes.find(
    (attr: any) =>
      attr.name === NEED_BASED_GRANT_NAMES.name ||
      attr.friendlyName === NEED_BASED_GRANT_NAMES.friendlyName ||
      attr.displayName === NEED_BASED_GRANT_NAMES.friendlyName ||
      attr.controlName === NEED_BASED_GRANT_NAMES.name
  );

  if (!needBasedGrantAttr) {
    return null;
  }

  const valueToStore = extractValue(needBasedGrantAttr);

  if (valueToStore) {
    localStorage.setItem(STORAGE_KEY, valueToStore);
    return valueToStore;
  }

  return null;
};
